import { useEffect, useRef } from "react";

declare global {
  interface Window {
    JitsiMeetExternalAPI: any;
  }
}

interface IUseJitsiMeetOptions {
  domain: string;
  roomName: string;
  jwt: string;
  parentNodeId: string;
  displayName?: string;
  onReadyToClose?: () => void;
}

export default function useJitsiMeet(options: IUseJitsiMeetOptions) {
  const { domain, roomName, jwt, parentNodeId, displayName, onReadyToClose } =
    options;
  const apiRef = useRef<any>(null);

  useEffect(() => {
    if (!domain || !roomName || !jwt || !parentNodeId) return;

    function initApi() {
      const parentNode = document.getElementById(parentNodeId);
      if (!parentNode) return;

      apiRef.current = new window.JitsiMeetExternalAPI(domain, {
        roomName,
        jwt,
        parentNode,
        width: "100%",
        height: "100%",
        configOverwrite: {
          defaultLanguage: "ro",
        },
        interfaceConfigOverwrite: {},
        userInfo: {
          displayName: displayName || "",
        },
      });

      apiRef.current.addEventListener("readyToClose", () => {
        if (onReadyToClose) onReadyToClose();
      });
    }

    const scriptId = `jitsi-external-api-${domain}`;
    const alreadyLoaded =
      !!window.JitsiMeetExternalAPI ||
      !!document.getElementById(scriptId);

    if (alreadyLoaded && window.JitsiMeetExternalAPI) {
      initApi();
    } else {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://${domain}/external_api.js`;
      script.async = true;
      script.onload = initApi;
      document.body.appendChild(script);
    }

    return () => {
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
  }, [domain, roomName, jwt, parentNodeId, displayName, onReadyToClose]);

  return apiRef;
}
